/**
 * Smooth opening and closing for Dawn's accordions: the product page rows (size
 * and fit, fabric and care, delivery), a collapsible "Pairs well with" row, and
 * the collapsible content section used on the FAQ page.
 *
 * CSS can only animate a <details> element to its natural height in Chrome
 * (interpolate-size), so on Safari and Firefox the rows would still snap. This
 * uses the Web Animations API instead, which current browsers all support.
 * Without JavaScript the rows still open and close, just instantly.
 *
 * A single delegated listener on the document also covers accordions added after
 * load, such as the product info Dawn re-renders when a variant changes.
 */
(() => {
  // The script is referenced from more than one section; set up only once.
  if (window.accordionAnimationsReady) return;
  window.accordionAnimationsReady = true;

  const DURATION = 250;
  const EASING = 'cubic-bezier(0.4, 0, 0.2, 1)';
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  // details element -> { animation, opening }
  const inFlight = new WeakMap();

  function setExpanded(summary, expanded) {
    summary.setAttribute('aria-expanded', expanded ? 'true' : 'false');
  }

  function animate(details, summary, content, opening) {
    const current = inFlight.get(details);

    // Where the content is right now. Mid-animation these are the animated values,
    // so a second click reverses smoothly from that point instead of jumping.
    let from = null;
    if (current) {
      const style = getComputedStyle(content);
      from = { height: content.offsetHeight, marginBottom: style.marginBottom, opacity: style.opacity };
      current.animation.cancel();
    }

    // Content only renders while [open] is set, so keep it open in both directions
    // and remove the attribute once a close has finished.
    details.open = true;
    const natural = { height: content.offsetHeight, marginBottom: getComputedStyle(content).marginBottom };

    const collapsed = { height: 0, marginBottom: '0px', opacity: 0 };
    const expanded = { height: natural.height, marginBottom: natural.marginBottom, opacity: 1 };
    const start = from || (opening ? collapsed : expanded);
    const end = opening ? expanded : collapsed;

    content.style.overflow = 'hidden';
    const animation = content.animate(
      [
        { height: `${start.height}px`, marginBottom: start.marginBottom, opacity: start.opacity },
        { height: `${end.height}px`, marginBottom: end.marginBottom, opacity: end.opacity },
      ],
      { duration: DURATION, easing: EASING, fill: 'forwards' }
    );

    inFlight.set(details, { animation, opening });
    setExpanded(summary, opening);

    // Clean up on the finished promise rather than the finish event. The promise
    // settles as soon as the animation reaches its end, while the event waits for
    // the browser to render a frame, which a background tab never does. That left
    // rows half-cleaned for anyone who switched tabs mid-animation.
    animation.finished.then(
      () => {
        if (inFlight.get(details)?.animation !== animation) return;
        inFlight.delete(details);
        if (!opening) details.open = false;
        // Dropping the fill hands control back to the stylesheet. It happens after
        // [open] is removed, so a closed row never flashes back to full height.
        animation.cancel();
        content.style.overflow = '';
      },
      () => {
        // Cancelled because the row was clicked again mid-animation. The animation
        // that replaced it owns the cleanup.
      }
    );
  }

  document.addEventListener('click', (event) => {
    if (event.defaultPrevented) return;

    const summary = event.target.closest('summary');
    if (!summary) return;

    const details = summary.parentElement;
    if (!details || details.tagName !== 'DETAILS' || !details.closest('.accordion')) return;
    // Leave any disclosure nested inside an accordion's content alone.
    if (details.parentElement.closest('details')) return;

    const content = summary.nextElementSibling;
    if (!content) return;

    if (reducedMotion.matches || typeof content.animate !== 'function') {
      // Let the native toggle happen, but keep aria-expanded right, including on
      // markup Dawn re-rendered after its own load-time setup ran.
      setExpanded(summary, !details.open);
      return;
    }

    event.preventDefault();
    const current = inFlight.get(details);
    const opening = current ? !current.opening : !details.open;
    animate(details, summary, content, opening);
  });
})();
